import { useState } from "react";
import { Plus, Trash2, Package, ChevronDown, Clock } from "lucide-react";
import { money } from "@/lib/api";
import { INVOICE_LINE_TYPES, LINE_TYPE_LABEL } from "@/lib/invoiceMeta";

const LABOUR_KINDS = ["labour", "service"];

export function InvoiceLineItems({ items, setItems, categories = [], onAddFromStock, vatRate = 0, vatEnabled = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const upd = (i, patch) => { const c = [...items]; c[i] = { ...c[i], ...patch }; setItems(c); };
  const add = (kind) => { setItems([...items, { description: "", qty: 1, unit_price: "", kind, category: "" }]); setMenuOpen(false); };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-bold tracking-widest text-muted-foreground">LINE ITEMS</span>
        <div className="flex items-center gap-3">
          {onAddFromStock && (
            <button type="button" onClick={onAddFromStock} data-testid="invoice-add-from-stock" className="inline-flex items-center gap-1 text-sm font-bold text-brand-2"><Package className="h-4 w-4" />Add part from stock</button>
          )}
          <div className="relative">
            <button type="button" onClick={() => setMenuOpen((o) => !o)} data-testid="invoice-add-line-menu" className="inline-flex items-center gap-1 rounded-md bg-brand px-2.5 py-1.5 text-sm font-bold text-white"><Plus className="h-4 w-4" />Add line<ChevronDown className="h-3.5 w-3.5" /></button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-lg border border-border bg-card shadow-lg" data-testid="invoice-add-line-options">
                  {INVOICE_LINE_TYPES.map((t) => (
                    <button type="button" key={t.id} onClick={() => add(t.id)} data-testid={`invoice-add-${t.id}`} className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary">{t.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {items.map((it, i) => {
          const isLabour = LABOUR_KINDS.includes(it.kind);
          const net = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
          return (
            <div key={i} className="rounded-lg border border-border p-2.5" data-testid={`invoice-item-${i}`}>
              <div className="flex gap-2">
                <input value={it.description} onChange={(e) => upd(i, { description: e.target.value })} placeholder={LINE_TYPE_LABEL[it.kind] || "Description"} className="min-w-0 flex-1 rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" data-testid={`invoice-item-desc-${i}`} />
                <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`invoice-item-del-${i}`} className="text-danger"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select value={it.kind} onChange={(e) => upd(i, { kind: e.target.value })} data-testid={`invoice-item-kind-${i}`} className="rounded-md border border-input px-2 py-1.5 text-xs outline-none">
                  {INVOICE_LINE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  {it.kind === "service" && <option value="service">Service</option>}
                </select>
                {categories.length > 0 && (
                  <select value={it.category || ""} onChange={(e) => upd(i, { category: e.target.value })} data-testid={`invoice-item-cat-${i}`} className="rounded-md border border-input px-2 py-1.5 text-xs outline-none">
                    <option value="">No category</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                )}
                {isLabour ? (
                  <div className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1" data-testid={`invoice-item-hours-wrap-${i}`}>
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <input type="number" value={it.hours ?? ""} onChange={(e) => { const h = e.target.value; upd(i, { hours: h, qty: h === "" ? it.qty : Number(h) }); }} placeholder="hrs" className="w-12 bg-transparent text-center text-xs outline-none" data-testid={`invoice-item-hours-${i}`} />
                    <span className="text-xs text-muted-foreground">×</span>
                    <input type="number" value={it.hourly_rate ?? ""} onChange={(e) => { const r = e.target.value; upd(i, { hourly_rate: r, unit_price: r === "" ? it.unit_price : Number(r) }); }} placeholder="rate" className="w-14 bg-transparent text-right text-xs outline-none" data-testid={`invoice-item-rate-${i}`} />
                  </div>
                ) : null}
                <div className="ml-auto flex items-center gap-2">
                  <input type="number" value={it.qty} onChange={(e) => upd(i, { qty: e.target.value })} className="w-14 rounded-md border border-input px-2 py-1.5 text-center text-sm outline-none focus:border-brand" data-testid={`invoice-item-qty-${i}`} />
                  <input type="number" value={it.unit_price} onChange={(e) => upd(i, { unit_price: e.target.value })} placeholder="price" className="w-20 rounded-md border border-input px-2 py-1.5 text-right text-sm outline-none focus:border-brand" data-testid={`invoice-item-price-${i}`} />
                  <span className="w-20 text-right text-sm font-bold text-midnight" data-testid={`invoice-item-total-${i}`}>{money(net)}</span>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="rounded-lg bg-secondary px-3 py-3 text-sm text-muted-foreground">No line items yet. Use “Add line” or “Add part from stock”.</p>}
      </div>
    </div>
  );
}
